import { useAccountState } from '../../redux/reducers/accountReducer';
import { useApi } from '../api/useApi';
import { Contact, Party, UserAccount } from '../types';
import { usePartyState } from '../../redux/reducers/partyReducer';
import { usePartyActions } from '../../redux/actions/partyActions';
import { ImportPartiesValidationResult, PartyImportInfo } from '../types/importTypes';
import { useToasts } from '../../components/toasts/useToasts';

export const PartyApiRoutes = {
    getParties: (accountId: string) => `account/${accountId}`,
    getParty: (accountId: string, partyId: string) => `account/${accountId}/party/${partyId}`,
    upsertParty: (accountId: string) => `account/${accountId}/party`,
    upsertParties: (accountId: string) => `account/${accountId}/parties`,
    addContactToParty: (accountId: string, partyId: string) => `account/${accountId}/party/${partyId}/contact`,
    addContactsToParty: (accountId: string, partyId: string) => `account/${accountId}/party/${partyId}/contacts`,
    deleteContactFromParty: (accountId: string, partyId: string, contactId: string) =>
        `account/${accountId}/party/${partyId}/contact/${contactId}`,
    deleteParty: (accountId: string, partyId: string) => `account/${accountId}/party/${partyId}`,
    deleteParties: (accountId: string) => `account/${accountId}/parties`,
    purgeParty: (accountId: string, partyId: string) => `account/${accountId}/party/${partyId}/purge`,
    purgeParties: (accountId: string) => `account/${accountId}/parties/purge`,
    validateImport: (accountId: string) => `account/${accountId}/validateImport`,
};

export type PartyService = {
    parties: Party[];
    loadPartiesAsync: () => Promise<void>;
    getPartyAsync: (partyId: string) => Promise<Party | undefined>;
    findParty: (partyId: string) => Party | undefined;
    upsertPartyAsync: (party: Party) => Promise<void>;
    upsertPartiesAsync: (parties: Party[]) => Promise<void>;
    addContactToPartyAsync: (partyId: string, contact: Contact, sendToast?: boolean) => Promise<void>;
    addContactsToPartyAsync: (partyId: string, contacts: Contact[], sendToast?: boolean) => Promise<void>;
    deleteContactFromPartyAsync: (partyId: string, contactId: string, sendToast?: boolean) => Promise<void>;
    deletePartyAsync: (partyId: string) => Promise<void>;
    deletePartiesAsync: (partyIds: string[]) => Promise<void>;
    purgePartyAsync: (partyId: string) => Promise<void>;
    purgePartiesAsync: (partyIds: string[]) => Promise<void>;
    validateImportAsync: (partyImports: PartyImportInfo[]) => Promise<ImportPartiesValidationResult>;
};
export const usePartyService = (): PartyService => {
    //Hooks
    const { primaryAccount } = useAccountState();
    const { account } = (primaryAccount as UserAccount) ?? {};
    const partyState = usePartyState();
    const partyActions = usePartyActions();
    const toasts = useToasts();

    //Api
    const api = useApi<Party>('api/Party');

    //Data Calls
    const loadPartiesAsync = async () => {
        partyActions.setParties([]);
        const parties = await api.getAsync(PartyApiRoutes.getParties(account?.accountId as string));
        partyActions.setParties(parties);
    };
    const getPartyAsync = async (partyId: string) => {
        let party = (partyState.parties || []).find((p: Party) => p.partyId === partyId);
        if (!party) {
            party = await api.getAsync(PartyApiRoutes.getParty(account?.accountId as string, partyId));
            if (party) partyActions.addOrReplaceParty(party);
        }
        return party;
    };
    const upsertPartyAsync = async (party: Party) => {
        partyActions.addOrReplaceParty(party);
        const updatedParty = await api.postAsync(party, PartyApiRoutes.upsertParty(account?.accountId as string));
        partyActions.addOrReplaceParty(updatedParty);
    };
    const upsertPartiesAsync = async (parties: Party[]) => {
        const updatedParties = await api.postAsync(parties, PartyApiRoutes.upsertParties(account?.accountId as string));
        updatedParties.forEach((party: Party) => partyActions.addOrReplaceParty(party));
    };
    const addContactToPartyAsync = async (partyId: string, contact: Contact, sendToast: boolean = false) => {
        const updatedParty = await api.postAsync(
            contact,
            PartyApiRoutes.addContactToParty(account?.accountId as string, partyId),
        );
        if (updatedParty) {
            partyActions.addOrReplaceParty(updatedParty);
        }

        if (sendToast) {
            toasts.notifySuccess('Contact added to Party', `${contact.name} was added to ${updatedParty?.name}`);
        }
    };
    const addContactsToPartyAsync = async (partyId: string, contacts: Contact[], sendToast: boolean = false) => {
        const updatedParty = await api.postAsync(
            contacts,
            PartyApiRoutes.addContactsToParty(account?.accountId as string, partyId),
        );
        if (updatedParty) partyActions.addOrReplaceParty(updatedParty);
        if (sendToast) {
            toasts.notifySuccess('Contacts added to Party', `${contacts.length} contacts were added to ${updatedParty?.name}`);
        }
    };
    const deleteContactFromPartyAsync = async (partyId: string, contactId: string, sendToast: boolean = false) => {
        const updatedParty = await api.deleteAsync(
            [],
            PartyApiRoutes.deleteContactFromParty(account?.accountId as string, partyId, contactId),
        );
        if (updatedParty) partyActions.addOrReplaceParty(updatedParty);
        if (sendToast) {
            toasts.notifySuccess('Contact deleted from Party', `Contact was deleted from ${updatedParty?.name}`);
        }
    };
    const deletePartyAsync = async (partyId: string) => {
        partyActions.deleteParty(partyId);
        await api.deleteAsync([partyId], PartyApiRoutes.deleteParty(account?.accountId as string, partyId));
    };
    const deletePartiesAsync = async (partyIds: string[]) => {
        partyActions.deleteParties(partyIds);
        await api.deleteAsync(partyIds, PartyApiRoutes.deleteParties(account?.accountId as string));
    };
    const purgePartyAsync = async (partyId: string) => {
        partyActions.deleteParty(partyId);
        await api.deleteAsync([partyId], PartyApiRoutes.purgeParty(account?.accountId as string, partyId));
    };
    const purgePartiesAsync = async (partyIds: string[]) => {
        partyActions.deleteParties(partyIds);
        await api.deleteAsync(partyIds, PartyApiRoutes.purgeParties(account?.accountId as string));
    };
    const findParty = (partyId: string) => {
        return (partyState.parties || []).find((p: Party) => p.partyId === partyId);
    };
    const validateImportAsync = async (partyImports: PartyImportInfo[]) => {
        const validationResult = await api.postAsync(
            partyImports,
            PartyApiRoutes.validateImport(account?.accountId as string),
        );
        return validationResult;
    };

    return {
        parties: partyState.parties || [],
        getPartyAsync,
        upsertPartyAsync,
        upsertPartiesAsync,
        deletePartyAsync,
        deletePartiesAsync,
        purgePartyAsync,
        purgePartiesAsync,
        loadPartiesAsync,
        findParty,
        addContactToPartyAsync,
        addContactsToPartyAsync,
        deleteContactFromPartyAsync,
        validateImportAsync,
    };
};
